import { MENSWEAR_SHOPPING_PREFERENCE, WOMENSWEAR_SHOPPING_PREFERENCE } from '../constants';

export interface ShoppingPreferenceDecision {
  showShoppingPreferenceView: boolean;
  shouldSetUserGender: boolean;
  userGenderToSet: 'male' | 'female' | null;
  setGenderWarning: boolean;
}

/**
 * Determines whether to show ShoppingPreferenceView and handles gender logic
 * based on brand gender_specific, user authentication status, and gender matching
 */
export function determineShoppingPreferenceLogic(
  genderSpecific: 'false' | 'm' | 'f',
  isAuthenticated: boolean,
  isOnboarded: boolean,
  userGenderLabel?: 'male' | 'female' | 'unknown',
  productGenderLabel?: 'male' | 'female' | 'unknown'
): ShoppingPreferenceDecision {
  // Log input parameters for debugging
  console.debug('🔍 [GENDER_LOGIC] Input parameters:', {
    gender_specific: genderSpecific,
    user_gender_label: userGenderLabel,
    product_gender_label: productGenderLabel,
    isAuthenticated,
    isOnboarded,
    isNewUser: !isAuthenticated || !isOnboarded
  });

  const isNewUser = !isAuthenticated || !isOnboarded;
  
  // Convert gender_specific to comparable format
  const brandGenderLabel = genderSpecific === 'm' ? 'male' : genderSpecific === 'f' ? 'female' : null;

  // Condition 1: gender_specific==='false' && newUser
  if (genderSpecific === 'false' && isNewUser && (productGenderLabel === 'unknown' || !productGenderLabel)) {
    console.debug('🔍 [GENDER_LOGIC] Triggered Condition 1a: Brand non-specific, new user -> Show shopping preference');
    return {
      showShoppingPreferenceView: true,
      shouldSetUserGender: false,
      userGenderToSet: null,
      setGenderWarning: false
    };
  }

  if (genderSpecific === 'false' && isNewUser && (productGenderLabel === 'female' || productGenderLabel === 'male')) {
    console.debug('🔍 [GENDER_LOGIC] Triggered Condition 1b: Brand non-specific, new user -> Show shopping preference');
    return {
      showShoppingPreferenceView: false,
      shouldSetUserGender: true,
      userGenderToSet: productGenderLabel,
      setGenderWarning: false
    };
  }

  // Condition 2: gender_specific==='m' or 'f' && newUser
  if ((genderSpecific === 'm' || genderSpecific === 'f') && isNewUser) {
    console.debug(`🔍 [GENDER_LOGIC] Triggered Condition 2: Brand ${genderSpecific}-specific, new user -> Set gender to ${brandGenderLabel}`);
    return {
      showShoppingPreferenceView: false,
      shouldSetUserGender: true,
      userGenderToSet: brandGenderLabel,
      setGenderWarning: false
    };
  }

  // For existing users (authenticated && onboarded)
  if (!isNewUser) {
    // Condition 3: gender_specific==='false' && (user_gender_label = product_gender_label)
    if (genderSpecific === 'false' && userGenderLabel === productGenderLabel) {
      console.debug('🔍 [GENDER_LOGIC] Triggered Condition 3: Brand non-specific, existing user, genders match -> Skip shopping preference');
      return {
        showShoppingPreferenceView: false,
        shouldSetUserGender: false,
        userGenderToSet: null,
        setGenderWarning: false
      };
    }

    // Condition 4: gender_specific==='false' && (user_gender_label != product_gender_label)
    if (genderSpecific === 'false' && userGenderLabel !== productGenderLabel) {
      console.debug('🔍 [GENDER_LOGIC] Triggered Condition 4: Brand non-specific, existing user, gender mismatch -> Show warning');
      return {
        showShoppingPreferenceView: false,
        shouldSetUserGender: false,
        userGenderToSet: null,
        setGenderWarning: true
      };
    }

    // Condition 5: gender_specific='m' or 'f' && (user_gender_label = brand_gender_specific)
    if ((genderSpecific === 'm' || genderSpecific === 'f') && userGenderLabel === brandGenderLabel) {
      console.debug(`🔍 [GENDER_LOGIC] Triggered Condition 5: Brand ${genderSpecific}-specific, existing user, genders match -> Skip shopping preference`);
      return {
        showShoppingPreferenceView: false,
        shouldSetUserGender: false,
        userGenderToSet: null,
        setGenderWarning: false
      };
    }

    // Condition 6: gender_specific='m' or 'f' && (user_gender_label != brand_gender_specific)
    if ((genderSpecific === 'm' || genderSpecific === 'f') && userGenderLabel !== brandGenderLabel) {
      console.debug(`🔍 [GENDER_LOGIC] Triggered Condition 6: Brand ${genderSpecific}-specific, existing user, gender mismatch -> Show warning`);
      return {
        showShoppingPreferenceView: false,
        shouldSetUserGender: false,
        userGenderToSet: null,
        setGenderWarning: true
      };
    }
  }

  // Default fallback - should not reach here normally
  console.debug('🔍 [GENDER_LOGIC] Fallback condition triggered - unexpected scenario');
  return {
    showShoppingPreferenceView: false,
    shouldSetUserGender: false,
    userGenderToSet: null,
    setGenderWarning: false
  };
}

/**
 * Converts gender label to shopping preference code
 */
export function getShoppingPreferenceFromGender(gender: 'male' | 'female'): string {
  return gender === 'male' ? MENSWEAR_SHOPPING_PREFERENCE : WOMENSWEAR_SHOPPING_PREFERENCE;
}

/**
 * Converts shopping preference code to gender label
 */
export function getGenderFromShoppingPreference(shoppingPreference: string): 'male' | 'female' | undefined {
  switch (shoppingPreference) {
    case MENSWEAR_SHOPPING_PREFERENCE:
      return 'male';
    case WOMENSWEAR_SHOPPING_PREFERENCE:
      return 'female';
    default:
      return undefined;
  }
}
